import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { apiErrorMessage } from '@/lib/clients';
import type { Permission } from '@/lib/permissions';
import { PERMISSION_GROUPS, PERMISSION_LABELS } from '@/lib/role-display';

/** Ligne de GET /custom-roles (tenants-service, listCustomRoles). */
export interface CustomRole {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  permissions: Permission[];
  createdById: string | null;
  createdAt: string;
  updatedAt: string;
  membersCount?: number;
}

export interface CustomRolesPage { data: CustomRole[]; page: number; pageSize: number; total: number; totalPages: number }

export interface CustomRoleInput { name: string; description?: string | null; permissions: Permission[] }

export interface DeleteCustomRoleResult { deleted: true; affectedMemberships: number; warning?: string }

export const GRANTABLE_PERMISSION_GROUPS = PERMISSION_GROUPS;

export const useCustomRoles = (enabled = true) =>
  useQuery({
    queryKey: ['custom-roles'],
    queryFn: async () => (await api.get<CustomRolesPage>('/custom-roles?pageSize=100')).data,
    enabled,
  });

export const useCreateCustomRole = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CustomRoleInput) => (await api.post<CustomRole>('/custom-roles', body)).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['custom-roles'] }),
  });
};

export const useUpdateCustomRole = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: Partial<CustomRoleInput> & { id: string }) =>
      (await api.patch<CustomRole>(`/custom-roles/${id}`, body)).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['custom-roles'] }),
  });
};

export const useDeleteCustomRole = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete<DeleteCustomRoleResult>(`/custom-roles/${id}`)).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['custom-roles'] });
      void qc.invalidateQueries({ queryKey: ['members'] });
    },
  });
};

const forbiddenDetails = (err: unknown): Record<string, unknown> | undefined => {
  const res = (err as { response?: { status?: number; data?: { error?: { details?: unknown } } } })?.response;
  const details = res?.status === 403 ? res.data?.error?.details : undefined;
  return details && typeof details === 'object' && !Array.isArray(details) ? details as Record<string, unknown> : undefined;
};

/** 403 canGrant : permissions que l'acteur tente d'accorder sans les posséder (details.missing). */
export const missingPermissions = (err: unknown): string[] => {
  const missing = forbiddenDetails(err)?.['missing'];
  return Array.isArray(missing) ? missing.filter((p): p is string => typeof p === 'string') : [];
};

export interface BlockingMember { membershipId: string; userId: string; missing: string[] }

/** 403 sur DELETE /custom-roles/:id : membres qui gagneraient des permissions que l'acteur n'a pas. */
export const blockingMembers = (err: unknown): BlockingMember[] => {
  const blocking = forbiddenDetails(err)?.['blocking'];
  return Array.isArray(blocking) ? blocking as BlockingMember[] : [];
};

export const permissionLabel = (p: string): string => PERMISSION_LABELS[p as Permission] ?? p;

/** Message d'erreur d'une écriture de droits : le refus canGrant nomme les permissions bloquées. */
export const grantErrorMessage = (err: unknown, fallback: string): string => {
  const missing = missingPermissions(err);
  return missing.length
    ? `Vous ne pouvez pas accorder ces permissions car vous ne les possédez pas vous-même : ${missing.map(permissionLabel).join(', ')}`
    : apiErrorMessage(err, fallback);
};
